import { get } from 'lodash/fp';
import { BOOKING_ID, REGEX_INS_ALL_CASES } from '@whitelabel/helpers/constants';
import {
  TRAVEL_POLICY_TYPES,
  PRODUCT_POLICY_TYPES,
  EVENT_POLICY_TYPES,
  PARCEL_POLICY_TYPES,
  WARRANTY_POLICY_TYPES,
  PARAMETRIC_POLICY_TYPES,
  SHOPEE_PARTNER_ID,
  PET_POLICY_TYPES,
  POLICY_TYPE,
  OLA_PARTNER_ID,
  INTERNAL_PARTNERS,
  ETRAVELI_PARTNER_ID,
  AMAZON_PARTNER_ID,
  STATUS_CONFIRMED,
} from './constants';
import messages from './messages';
import { dateISOtoAge } from './date';
import { IClaimBooking } from './types/claimDetailsTypes';
import {
  PolicyTypes,
  IBooking,
  IBookingCardAttributes,
  IBookingCardAttributesDetails,
  IPolicyTypes,
  IQuote,
  ICOIResponse,
} from './types';

export const getPolicyTitle = (policy: any) => get('content.title', policy) || policy.policyName;

export const getPolicyNames = (booking: IClaimBooking | null) =>
  booking?.quotes
    ?.filter(({ status }) => status === STATUS_CONFIRMED)
    .map(({ policy }) => getPolicyTitle(policy))
    .join('; ');

export const getAllQuotesFromBooking = (booking: any) => {
  const policyTypes = booking && Object.values(booking.policyTypes);
  // @ts-ignore ts-migra./types/IBookingsunknown' is not assignable to type '{ quotes: any; }'... Remove this comment to see the full error message
  return policyTypes && policyTypes?.flatMap(({ quotes }: { quotes: any }) => quotes);
};

export const getInsuredLabel = (policyType: any, insured: any, intl: any, partnerId: any) => {
  if (SHOPEE_PARTNER_ID === partnerId) {
    return intl.formatMessage(messages.insuredLabelShopee, { itemCount: 1 });
  }

  if (TRAVEL_POLICY_TYPES.includes(policyType)) {
    return intl.formatMessage(messages.insuredLabelTravel, { itemCount: Array.isArray(insured) ? insured.length : 1 });
  }

  if (EVENT_POLICY_TYPES.includes(policyType)) {
    return intl.formatMessage(messages.insuredLabelEvent, { itemCount: 1 });
  }

  if (PRODUCT_POLICY_TYPES.includes(policyType)) {
    return intl.formatMessage(messages.insuredLabelProduct, { itemCount: 1 });
  }

  if (PARCEL_POLICY_TYPES.includes(policyType)) {
    return intl.formatMessage(messages.insuredLabelParcel, { itemCount: 1 });
  }

  if (PARAMETRIC_POLICY_TYPES.includes(policyType)) {
    return intl.formatMessage(messages.insuredLabelLocation, { itemCount: 1 });
  }
  if (PET_POLICY_TYPES.includes(policyType)) {
    return intl.formatMessage(messages.insuredLabelPet);
  }

  return intl.formatMessage(messages.insuredLabelDefault, { itemCount: insured ? insured.length : 1 });
};

export const getInsuredItems = (
  policyType: any,
  insured: any,
  attributes: any,
  confirmedAt: any,
  intl: any,
  partnerTransactionId: any,
) => {
  const {
    brand,
    model,
    description,
    eventName,
    serialNumber,
    // @ts-ignore ts-migrate(2525) FIXME: Initializer provides no value for this binding ele... Remove this comment to see the full error message
    policyholder: { address1, city, country, postcode } = {},
    petName,
  } = attributes;

  if (TRAVEL_POLICY_TYPES.includes(policyType) && insured) {
    return insured.map(({ firstName, lastName, birthDate }: any) => ({
      name: `${firstName} ${lastName}`,
      attributes: birthDate ? [dateISOtoAge(birthDate, confirmedAt)] : [],
    }));
  }

  if (EVENT_POLICY_TYPES.includes(policyType) && eventName) {
    return [{ name: eventName, attributes: [] }];
  }

  if (PRODUCT_POLICY_TYPES.includes(policyType)) {
    if (brand || model) {
      return [{ name: [brand, model].join(' ').trim(), attributes: [] }];
    }
    if (description) return [{ name: description, attributes: [] }];
  }

  if (PARCEL_POLICY_TYPES.includes(policyType) && partnerTransactionId) {
    return [{ name: `${intl.formatMessage(messages.insuredItemParcel)}${partnerTransactionId}`, attributes: [] }];
  }

  if (WARRANTY_POLICY_TYPES.includes(policyType) && serialNumber) {
    return [{ name: `${intl.formatMessage(messages.insuredItemTracker)} ${serialNumber}`, attributes: [] }];
  }

  if (PARAMETRIC_POLICY_TYPES.includes(policyType)) {
    if (address1) return [{ name: `${address1}, ${city}, ${country}, ${postcode}`, attributes: [] }];
    if (country) return [{ name: `${country}, ${postcode}`, attributes: [] }];
  }

  if (PET_POLICY_TYPES.includes(policyType) && petName) {
    return [{ name: petName, attributes: [] }];
  }

  return [];
};

export const getQuote = (quoteID: any, booking: any) =>
  // @ts-ignore ts-migrate(2571) FIXME: Object is of type 'unknown'.
  Object.values(booking.policyTypes)
    // @ts-ignore ts-migrate(2571) FIXME: Object is of type 'unknown'.
    .reduce((quotes, policyType) => quotes.concat(policyType.quotes), [])
    // @ts-ignore Manually added. Remove this comment to see the full error message.
    .find((quote) => quoteID === quote.id);

export const shapeBookingAttributes = (attributes: IBookingCardAttributes[]): IBookingCardAttributesDetails[] =>
  attributes.map(({ value }) => ({ ...value }));

export const getBookingPremiumsProps = (coiResponse: ICOIResponse): any => {
  const { currency, totalPremium, totalPrice, taxes, coiPage, partner, totalSurcharge } = coiResponse;

  return {
    currency,
    totalPremium,
    totalPrice,
    totalSurcharge,
    taxes,
    premiumLabel: coiPage?.priceLabel,
    totalLabel: coiPage?.totalLabel,
    partnerId: partner.id,
    hideTaxWhenZero: coiPage?.hideTaxWhenZero,
  };
};

export const hasCOVIDPolicy = (policyTypes: PolicyTypes | any): boolean =>
  Object.keys(policyTypes).includes(POLICY_TYPE.TRAVEL_COVID);

export const hasPolicyType = (policyTypes: IPolicyTypes, policyType: string): boolean =>
  Object.keys(policyTypes).includes(policyType);

/* 
  - if one or more quote is cancellable then return true.
  - if all quotes are not cancellable then return false.
  */
export const isBookingCancellable = (booking: IBooking) => {
  const policyTypes = booking && Object.values(booking.policyTypes);
  return policyTypes?.some(({ quotes }: { quotes: IQuote[] }) =>
    Object.values(quotes).some(({ canBeCancelled }) => canBeCancelled),
  );
};

export const isBookingModifiable = (booking: IBooking) => {
  const policyTypes = booking && Object.values(booking.policyTypes);
  return policyTypes?.some(({ quotes }: { quotes: IQuote[] }) =>
    quotes.some((quote: IQuote) => quote.bookingModificationEnabled),
  );
};

export const isPolicyHolderModifiable = (booking: IBooking) => {
  const { allowUpdates } = booking?.policyholder || {};
  return allowUpdates;
};

// TODO: will refactor this function once xcore flag is ready(XCOM-2176)
export const checkPermissionForModification = (booking: IBooking) => {
  const allowedMainPartnerIds = [ETRAVELI_PARTNER_ID, AMAZON_PARTNER_ID];
  const {
    policyTypes,
    partner: { subsidiary, id: mainPartnerId },
  } = booking;
  return (
    (OLA_PARTNER_ID === subsidiary.id && hasPolicyType(policyTypes, POLICY_TYPE.FLIGHT_DELAY_COVER)) ||
    INTERNAL_PARTNERS.includes(subsidiary.id) ||
    (allowedMainPartnerIds.includes(mainPartnerId) && !hasCOVIDPolicy(policyTypes))
  );
};

export const storeINSInLocalStorage = () => {
  const url = window.location.href;
  const INS = url.match(REGEX_INS_ALL_CASES)?.[0].toUpperCase();

  if (INS) {
    localStorage.setItem(BOOKING_ID, INS);
  }
};
